import type { Block } from "building-types/blocks/blockTypes";
import {
  ComponentVerticalPlacementType,
  type ComponentInstance,
} from "building-types/components/componentInstanceTypes";
import type { Doc } from "building-types/doc";
import type { DocForeignHashJoinTable } from "building-types/doc/docForeignKeyTable";
import type { ComponentOwnedMemberEntityType } from "building-types/handles/docEntityHandleTypes";
import type { ForeignEntityId } from "building-types/handles/foreignEntityId";
import type { StaticRoom } from "building-types/rooms/staticRoomTypes";
import type { Table } from "common/data/table";
import { docEntityHandleHash } from "./entity/core/handleHash";
import { parseEntityInfoFromHandleHash } from "./entity/docEntityHandleHash";

type ComponentMembers = DocForeignHashJoinTable<ComponentOwnedMemberEntityType, ForeignEntityId<"COMPONENT">>;

interface ComponentSourceLevelTables {
  readonly componentMembers: ComponentMembers;
  readonly componentInstances: Table<ComponentInstance>;
  readonly rooms: Table<StaticRoom>;
  readonly blocks: Table<Block>;
}

function getComponentRooms(componentId: string, componentMembers: ComponentMembers, rooms: Table<StaticRoom>) {
  const componentRooms: StaticRoom[] = [];

  for (const roomId in rooms) {
    const hash = docEntityHandleHash({ type: "ROOM", id: roomId });
    if (componentMembers[hash] !== componentId) {
      continue;
    }

    componentRooms.push(rooms[roomId]);
  }

  return componentRooms;
}

interface CollectComponentSourceLevelsProps extends ComponentSourceLevelTables {
  readonly componentId: string;
  /**
   * Remapping of the instance the levels are being collected for. Only applied when the instance is
   * nested inside another component, where the parent sees the remapped (host) levels rather than
   * the nested component's own source levels.
   */
  readonly levelRemapping: Table<string>;
  readonly isNestedComponentInstance: boolean;
}

function collectComponentSourceLevels({
  componentId,
  componentMembers,
  componentInstances,
  rooms,
  blocks,
  levelRemapping,
  isNestedComponentInstance,
}: CollectComponentSourceLevelsProps): Set<string> {
  const sourceLevels = new Set<string>();

  // TODO: this loop over the rooms is only necessary because there are
  // still some old files with rooms on levels that are not component members.
  const componentRooms = getComponentRooms(componentId, componentMembers, rooms);
  for (const room of componentRooms) {
    const { parentId } = room;
    if (!parentId) {
      continue;
    }

    const block = blocks[parentId];
    if (!block) {
      continue;
    }

    const { levelId: sourceLevelId } = block;

    const levelId = isNestedComponentInstance ? levelRemapping[sourceLevelId] ?? sourceLevelId : sourceLevelId;

    sourceLevels.add(levelId);
  }

  for (const hash in componentMembers) {
    if (componentMembers[hash] !== componentId) {
      continue;
    }
    const entityInfo = parseEntityInfoFromHandleHash(hash);
    if (entityInfo && entityInfo.entityType === "LEVEL") {
      const { entityId: sourceLevelId } = entityInfo;
      const levelId = isNestedComponentInstance ? levelRemapping[sourceLevelId] ?? sourceLevelId : sourceLevelId;
      sourceLevels.add(levelId);
    }
  }

  // look for nested component instances
  for (const hash in componentMembers) {
    if (componentId !== componentMembers[hash]) {
      continue;
    }

    const entityInfo = parseEntityInfoFromHandleHash(hash);
    if (!entityInfo || entityInfo.entityType !== "COMPONENT_INSTANCE") {
      continue;
    }

    const nestedComponentInstance = componentInstances[entityInfo.entityId];
    if (!nestedComponentInstance) {
      continue;
    }

    const nestedSourceLevels = buildComponentSourceLevels({
      componentInstance: nestedComponentInstance,
      componentMembers,
      componentInstances,
      rooms,
      blocks,
      isNestedComponentInstance: true,
    });

    for (const levelId of nestedSourceLevels) {
      sourceLevels.add(levelId);
    }
  }

  return sourceLevels;
}

interface BuildComponentSourceLevelsProps extends ComponentSourceLevelTables {
  readonly componentInstance: ComponentInstance;
  readonly isNestedComponentInstance?: boolean;
}

/**
 * The levels a component instance sources its content from.
 *
 * For a nested instance the instance's own level remapping is applied, so the containing component
 * sees the levels the nested content lands on rather than the nested component's own levels.
 */
export function buildComponentSourceLevels({
  componentInstance,
  componentMembers,
  componentInstances,
  rooms,
  blocks,
  isNestedComponentInstance = false,
}: BuildComponentSourceLevelsProps): Set<string> {
  const { componentId, verticalPlacement } = componentInstance;

  const levelRemapping =
    verticalPlacement.type === ComponentVerticalPlacementType.REMAPPING ? verticalPlacement.levelRemapping ?? {} : {};

  return collectComponentSourceLevels({
    componentId,
    componentMembers,
    componentInstances,
    rooms,
    blocks,
    levelRemapping,
    isNestedComponentInstance,
  });
}

/**
 * The levels a component definition sources its content from, independent of any one instance.
 *
 * This is the instance-agnostic counterpart to {@link buildComponentSourceLevels}: an instance's own
 * remapping never changes which levels its component owns, so the same set is shared by every
 * instance of the component.
 */
export function docComponentSourceLevels(doc: Doc, componentId: string): Set<string> {
  return collectComponentSourceLevels({
    componentId,
    componentMembers: doc.componentMembers,
    componentInstances: doc.componentInstances,
    rooms: doc.rooms,
    blocks: doc.blocks,
    levelRemapping: {},
    isNestedComponentInstance: false,
  });
}

/** True when the component spans more than one source level, which Level Remapping cannot express. */
export function docComponentIsMultiLevel(doc: Doc, componentId: string): boolean {
  return docComponentSourceLevels(doc, componentId).size > 1;
}
